import React, { useState } from 'react';
import '../styles/Projects.css';
import '../styles/ScrollAnimation.css';
import ScrollAnimation from '../utils/ScrollAnimation';

const FILTERS = ['All', 'Full-Stack', 'Backend'];

const PROJECTS = [
  {
    title: 'GanarPro',
    category: 'Full-Stack',
    summary: 'Construction project management — rebuilt core modules, CI/CD, and Celery jobs.',
    impact: '500+ active projects',
    stack: ['Django', 'React', 'PostgreSQL', 'Celery'],
    link: 'https://www.ganarpro.com/',
    logo: '/project-logos/ganarpro.png',
  },
  {
    title: 'MovSentry',
    category: 'Full-Stack',
    summary:
      'Cybersecurity training platform with multi-channel simulations and tenant architecture.',
    impact: '10K+ users trained',
    stack: ['Django', 'React', 'Redis', 'AWS'],
    link: 'http://movsentry.com/',
    logo: '/project-logos/movsentry.svg',
  },
  {
    title: 'Supplo',
    category: 'Backend',
    summary:
      'Semantic supplier search for manufacturers — Elasticsearch + OpenAI meaning-based retrieval, with filters, pagination, and live re-indexing when database records change.',
    impact: '500K+ raw materials searchable',
    stack: ['Django', 'Elasticsearch', 'OpenAI', 'Semantic Search'],
    link: 'https://www.supplo.co/',
    logo: '/project-logos/supplo.png',
  },
  {
    title: 'CraftPositions',
    category: 'Full-Stack',
    summary: 'Trades hiring marketplace with matching, video interviews, and escrow payments.',
    impact: '60% faster time-to-hire',
    stack: ['Django', 'React', 'Stripe', 'Jitsi'],
    link: 'https://craftpositions.com/',
    logo: '/project-logos/craftpositions.png',
  },
  {
    title: 'Express Subscriptions',
    category: 'Backend',
    summary: 'Shopify subscription billing with retries, pause/resume, and dunning flows.',
    impact: '40% better retention',
    stack: ['Rails', 'PostgreSQL', 'Shopify'],
    logo: '/project-logos/express-subscriptions.svg',
  },
  {
    title: 'Supplier Sync',
    category: 'Backend',
    summary: 'Multi-supplier inventory sync for dropshippers at high volume.',
    impact: '100K+ products/day',
    stack: ['Rails', 'MongoDB', 'Sidekiq'],
    logo: '/project-logos/supplier-sync.svg',
  },
  {
    title: 'La Beau POS',
    category: 'Full-Stack',
    summary: 'Salon POS with conflict-aware scheduling and end-of-day reconciliation.',
    impact: '80% fewer conflicts',
    stack: ['Rails', 'React', 'AWS S3'],
    link: 'https://labeaupos.com/',
    logo: '/project-logos/labeaupos.png',
  },
  {
    title: 'BusinessMajlis',
    category: 'Full-Stack',
    summary:
      'AI matching platform for MENA businesses — sellers, buyers, finance partners, and advisors log in, get matched by profile fit, then chat with each other and with admin.',
    impact: 'Role-based matching & messaging',
    stack: ['Django', 'React', 'PostgreSQL', 'OpenAI', 'PostHog'],
    link: 'https://businessmajlis.ai/',
    logo: '/project-logos/businessmajlis.png',
  },
  {
    title: 'E-Medical',
    category: 'Full-Stack',
    summary: 'Verifiable digital medical certificates with QR checks and doctor signatures.',
    impact: 'Mobile-first flow',
    stack: ['Django', 'React', 'Cloudinary'],
    link: 'https://e-medicalcertificate.com/',
    logo: '/project-logos/emedical.png',
  },
  {
    title: 'PetroDials',
    category: 'Full-Stack',
    summary:
      'Petrol pump management software — daily fuel & side sales, stock, credit parties, expenses, employee access, and PDF reports on one dashboard.',
    impact: '100+ petrol pumps',
    stack: ['Full-Stack', 'Dashboard', 'Reports', 'Multi-user'],
    link: 'https://petrodials.vercel.app/',
    logo: '/project-logos/petrodials.png',
  },
];

const Projects = () => {
  const [filter, setFilter] = useState('All');
  const [brokenLogos, setBrokenLogos] = useState({});
  const visible =
    filter === 'All' ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  const markBroken = (title) => {
    setBrokenLogos((prev) => ({ ...prev, [title]: true }));
  };

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <ScrollAnimation className="fade-up">
          <div className="projects-header">
            <div>
              <h2 className="section-title">Crafting Digital</h2>
              <div className="divider-glow" />
              <p className="section-subtitle">
                Transforming complex challenges into elegant solutions through thoughtful
                engineering and strategic thinking.
              </p>
            </div>
            <div className="projects-filters" role="tablist" aria-label="Filter projects">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={filter === f}
                  className={`projects-filter${filter === f ? ' is-active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </ScrollAnimation>

        <div className="projects-grid">
          {visible.map((project, index) => (
            <ScrollAnimation key={project.title} className={`fade-up stagger-${(index % 4) + 1}`}>
              <article className="project-card">
                <div className="project-card-media">
                  {project.logo && !brokenLogos[project.title] ? (
                    <img
                      src={project.logo}
                      alt={`${project.title} logo`}
                      className="project-logo"
                      onError={() => markBroken(project.title)}
                    />
                  ) : (
                    <span className="project-logo-fallback" aria-hidden="true">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  )}
                </div>
                <div className="project-card-body">
                  <div className="project-card-top">
                    <h3>{project.title}</h3>
                    <span>{project.category}</span>
                  </div>
                  <p>{project.summary}</p>
                  <p className="project-impact">{project.impact}</p>
                  <ul>
                    {project.stack.map((tech) => (
                      <li key={tech}>{tech}</li>
                    ))}
                  </ul>
                  {project.link && (
                    <a href={project.link} target="_blank" rel="noopener noreferrer">
                      Explore Project →
                    </a>
                  )}
                </div>
              </article>
            </ScrollAnimation>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
